// Client-side GOÄ rules for editing service lines before they're saved — the invoice page's
// service editor and manual invoice creation both use these. The backend stays authoritative
// (services/goaConflictService.ts, services/goaFactorRange.ts); these only give live feedback.

import { fold, foldExpanded } from "./textFold";

export interface GoaCode {
    id: string;
    nr: string;
    section: string;
    description: string;
    euroAmount: number;
    /** "Nur mit dem einfachen Gebührensatz berechnungsfähig" — billed at factor 1. */
    singleRate?: boolean;
    /** Listed in Abschnitt A — printed in another section, but capped at 2.5x like sections E and O. */
    sectionA?: boolean;
}

export interface ExclusionRule {
    nr: string;
    excludesWithCodes: string[];
    note: string;
}

export interface MaxCountRule {
    nr: string;
    maxCount: number;
    requiresAnyOf?: string[] | null;
    note: string;
}

export interface GoaConflict {
    codes: [string, string];
    note: string;
}

export interface GoaMaxCountViolation {
    nr: string;
    note: string;
    kind: "overLimit" | "missingRequiredCode";
    maxCount?: number;
    billedCount?: number;
    requiresAnyOf?: string[];
}

// Zuschlag codes carry an internal "<Section>-" disambiguation prefix (e.g. "B-A"
// for Zuschlag A) that isn't real GOÄ notation — the stored code stays "B-A" (needed
// for factor-range/conflict lookups), but never show the prefix to a human.
export const displayCode = (code: string) => (code || "").replace(/^[A-Z]-(?=[A-Z])/, "");

/** Base codes as the GOÄ names them: "410" or "410–418". */
export const requiredCodesLabel = (codes: string[] = []) =>
    codes.length > 1 ? `${displayCode(codes[0])}–${displayCode(codes[codes.length - 1])}` : displayCode(codes[0] ?? "");

/**
 * GOÄ code search: digits only → codes starting with them (an exact code first); as soon as the
 * query has a letter → title search, every word must appear in the description (case, accents and
 * umlaut spellings folded).
 */
export function searchGoaCatalog(catalog: GoaCode[], query: string, limit = 30): GoaCode[] {
    const term = query.trim();
    if (!term) return [];
    if (/^\d+$/.test(term)) {
        const matches = catalog.filter((c) => displayCode(c.nr).startsWith(term));
        const exact = matches.filter((c) => displayCode(c.nr) === term);
        return [...exact, ...matches.filter((c) => displayCode(c.nr) !== term)].slice(0, limit);
    }
    const words = term.split(/\s+/);
    return catalog
        .filter((c) => {
            const texts = [fold(c.description), foldExpanded(c.description)];
            return words.every((w) => texts.some((s) => s.includes(fold(w)) || s.includes(foldExpanded(w))));
        })
        .slice(0, limit);
}

// Strips technical PAD prefixes from imported annotation text (same cleanup as ExamTable.tsx).
export const stripPrefixes = (text: string) =>
    (text || "")
        .replace(/^\s*(Text|Begründungstext|Begründung|Begr|Berg)\.?\s*:\s*/i, "")
        .trim();

// A bare or SK-prefixed G/H/L/M/P/E (see pad/createExams.ts / examController.ts's
// normalizeFlatFeeCode) is a flat-fee flag, not a real GOÄ Ziffer — none of the rules built
// for real codes apply to it: the same flag legitimately repeats many times on one date
// (real MEDISTAR exports do this routinely), and it has no real Steigerungsfaktor at all
// (parsers.ts hardcodes factor to 0 for these), so the 1–3.5 range check doesn't apply either.
const FLAT_FEE_CODES = new Set(["G", "H", "L", "M", "P", "E", "SKG", "SKH", "SKL", "SKM", "SKP", "SKE"]);
export const isFlatFeeCode = (code: string) => FLAT_FEE_CODES.has((code || "").trim().toUpperCase());

/** Mirrors backend getAllowedFactorRange — client-side UX assist, backend remains authoritative. */
export function allowedFactorRange(code: string, catalog: Map<string, GoaCode>): { min: number; max: number; default: number; threshold: number } {
    const entry = catalog.get(code);
    const range = (max: number, threshold: number) => ({ min: 1, max, default: entry?.singleRate ? 1 : threshold, threshold });
    if (code === "437") return range(1.3, 1.15);
    if (entry?.sectionA) return range(2.5, 1.8);
    const section = entry?.section;
    if (!section) return range(3.5, 2.3);
    if (section === "M") return range(1.3, 1.15);
    if (section === "E" || section === "O") return range(2.5, 1.8);
    return range(3.5, 2.3);
}

/** Pairwise "not billable together" check — mirrors backend findGoaConflicts, scoped to
 * one date's rows (callers pass one treatment date at a time, so no day-bucketing needed). */
export function findConflicts(rows: { code: string }[], rules: ExclusionRule[]): GoaConflict[] {
    const codeSet = new Set(rows.map((r) => r.code).filter(Boolean));
    if (codeSet.size < 2) return [];
    const seen = new Set<string>();
    const conflicts: GoaConflict[] = [];
    for (const rule of rules) {
        if (!codeSet.has(rule.nr)) continue;
        for (const other of rule.excludesWithCodes) {
            if (!codeSet.has(other)) continue;
            const pair = [rule.nr, other].sort() as [string, string];
            const pairKey = pair.join("|");
            if (seen.has(pairKey)) continue;
            seen.add(pairKey);
            conflicts.push({ codes: pair, note: rule.note });
        }
    }
    return conflicts;
}

/** "Je Sitzung höchstens Nx" frequency-cap check — mirrors backend findGoaMaxCountViolations,
 * scoped to one date's rows. Sums quantity per code since one row can represent
 * multiple units of the same code (e.g. GOÄ 420 billed 3x, one row with quantity 3). */
export function findMaxCountViolations(rows: { code: string; quantity: number }[], rules: MaxCountRule[]): GoaMaxCountViolation[] {
    const counts = new Map<string, number>();
    for (const r of rows) {
        if (!r.code) continue;
        counts.set(r.code, (counts.get(r.code) ?? 0) + r.quantity);
    }
    const violations: GoaMaxCountViolation[] = [];
    for (const rule of rules) {
        const billedCount = counts.get(rule.nr) ?? 0;
        if (billedCount === 0) continue;
        if (billedCount > rule.maxCount) {
            violations.push({ nr: rule.nr, note: rule.note, kind: "overLimit", maxCount: rule.maxCount, billedCount });
        }
        if (rule.requiresAnyOf?.length && !rule.requiresAnyOf.some((code) => counts.has(code))) {
            violations.push({ nr: rule.nr, note: rule.note, kind: "missingRequiredCode", requiresAnyOf: rule.requiresAnyOf });
        }
    }
    return violations;
}

/** Codes on more than one line of an exam date — allowed, but worth a warning (a time or organ in
 * the Begründung tells the lines apart). Flat-fee flags repeat by design, so they're never counted. */
export function findDuplicateCodes(rows: { code: string }[]): Set<string> {
    const counts = new Map<string, number>();
    for (const r of rows) {
        if (!r.code.trim() || isFlatFeeCode(r.code)) continue;
        counts.set(r.code, (counts.get(r.code) ?? 0) + 1);
    }
    return new Set([...counts.entries()].filter(([, n]) => n > 1).map(([code]) => code));
}
